/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Interactive Preview - Real-time logo positioning with red box constraints
 * Updated: 2026-01-09
 *
 * NEW LOGIC:
 * - Renders mask image (with red boxes) as background
 * - Detects red box boundaries from mask image
 * - Draws ONE logo per red box boundary
 * - Logo is color-transformed based on printType
 * - User can drag/zoom logo within constraints
 * - Max zoom = 110% (allows oversized logos)
 * - Supports pinch-to-zoom on mobile
 */

import React, { useRef, useState, useEffect } from 'react';
import { RotateCcw, ZoomIn, ZoomOut, AlertCircle, Undo, Redo, X } from 'lucide-react';
import { getRedBoxBoundaries } from '../../services/cupCustomizerMockup';
import { applyLaserTransform, applySilkTransform } from '../../services/ImageColorTransform';

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface InteractivePreviewProps {
  cupTemplateUrl: string;      // Clean cup template (for final mockup)
  maskUrl: string;              // Mask image with red boxes (for preview)
  logoImage?: string;           // Logo base64
  printType: 'laser' | 'silk';  // Print type for color transform
  position: { x: number; y: number };
  scale: number;
  onPositionChange: (pos: { x: number; y: number }) => void;
  onScaleChange: (scale: number) => void;
  onClear?: () => void;         // New prop to clear logo
  detailThreshold?: number;     // Detail threshold for laser (0-255, default 50)
  onDetailThresholdChange?: (threshold: number) => void;
  disabled?: boolean;
}

const InteractivePreview: React.FC<InteractivePreviewProps> = ({
  cupTemplateUrl,
  maskUrl,
  logoImage,
  printType,
  position,
  scale,
  onPositionChange,
  onScaleChange,
  onClear,
  detailThreshold = 50,
  onDetailThresholdChange,
  disabled = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false); // True if dragging logo, False if panning canvas
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [redBoxes, setRedBoxes] = useState<BoundingBox[]>([]);
  const [boundaryWarning, setBoundaryWarning] = useState<string>('');
  const [transformedLogo, setTransformedLogo] = useState<string>('');
  const [showDebug, setShowDebug] = useState(false); // Hide debug boxes by default

  // History for Undo/Redo
  const [history, setHistory] = useState<{ position: { x: number; y: number }; scale: number }[]>([]);
  const [redoStack, setRedoStack] = useState<{ position: { x: number; y: number }; scale: number }[]>([]);

  // Touch/pinch state
  const [isPinching, setIsPinching] = useState(false);
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialScale, setInitialScale] = useState(100);

  // Canvas zoom state (separate from logo scale)
  const [canvasZoom, setCanvasZoom] = useState(100);

  // Canvas panning state (when zoomed and dragging outside logo)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  // Logo image for dimension calculation
  const [logoImg, setLogoImg] = useState<HTMLImageElement | null>(null);

  // Load logo image for dimension calculation
  useEffect(() => {
    if (!transformedLogo) {
      setLogoImg(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      setLogoImg(img);
    };
    img.src = transformedLogo;
  }, [transformedLogo]);

  // Calculate logo bounds for hit detection
  const getLogoBounds = (): { x: number; y: number; width: number; height: number } | null => {
    if (!logoImg || !redBoxes || redBoxes.length === 0 || !dimensions.width) {
      return null;
    }

    const box = redBoxes[0];
    const zoomFactor = canvasZoom / 100;
    const scaleFit = Math.min(dimensions.width / 512, dimensions.height / 512) * 0.9;

    // Mask display dimensions after zoom
    const drawWidth = 512 * scaleFit * zoomFactor;
    const drawHeight = 512 * scaleFit * zoomFactor;
    const maskX = (dimensions.width / 2) - (drawWidth / 2) + panOffset.x;
    const maskY = (dimensions.height / 2) - (drawHeight / 2) + panOffset.y;

    // Calculate logo bounds in display space
    const DETECTION_SIZE = 1024;
    const scaleX = drawWidth / DETECTION_SIZE;
    const scaleY = drawHeight / DETECTION_SIZE;

    const baseWidth = box.width * scaleX * 0.8;
    const aspectRatio = logoImg.height / logoImg.width;
    const scaleFactor = scale / 100;
    let logoWidth = baseWidth * scaleFactor;
    let logoHeight = logoWidth * aspectRatio;

    if (logoHeight > box.height * scaleY * 0.8 * scaleFactor) {
      logoHeight = box.height * scaleY * 0.8 * scaleFactor;
      logoWidth = logoHeight / aspectRatio;
    }

    const boxCenterX = maskX + (box.x + box.width / 2) * scaleX;
    const boxCenterY = maskY + (box.y + box.height / 2) * scaleY;

    const positionX = position.x * scaleX;
    const positionY = position.y * scaleY;
    const logoX = boxCenterX + positionX - logoWidth / 2;
    const logoY = boxCenterY + positionY - logoHeight / 2;

    return { x: logoX, y: logoY, width: logoWidth, height: logoHeight };
  };

  // Check if click is on logo
  const isClickOnLogo = (clientX: number, clientY: number): boolean => {
    if (!canvasRef.current) return false;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const logoBounds = getLogoBounds();
    if (!logoBounds) return false;

    return (
      x >= logoBounds.x &&
      x <= logoBounds.x + logoBounds.width &&
      y >= logoBounds.y &&
      y <= logoBounds.y + logoBounds.height
    );
  };

  // Calculate dynamic bounds based on red box and logo size
  const calculatePositionBounds = (): { minX: number; maxX: number; minY: number; maxY: number } => {
    if (!redBoxes || redBoxes.length === 0 || !logoImg) {
      // No bounds if no red box or logo
      return { minX: -150, maxX: 150, minY: -150, maxY: 150 };
    }

    // Use first red box (usually only one for cups)
    const box = redBoxes[0];

    // Calculate logo dimensions in detection space (1024x1024)
    // At scale%, logo = box.width * 0.8 * (scale/100)
    const baseWidth = box.width * 0.8;
    const aspectRatio = logoImg.height / logoImg.width;
    const scaleFactor = scale / 100;
    let logoWidth = baseWidth * scaleFactor;
    let logoHeight = logoWidth * aspectRatio;

    // Ensure logo fits (same logic as drawing code)
    if (logoHeight > box.height * 0.8 * scaleFactor) {
      logoHeight = box.height * 0.8 * scaleFactor;
      logoWidth = logoHeight / aspectRatio;
    }

    // Calculate max offset from center (in detection space)
    // Logo must stay completely inside box
    // Box spans from (box.x, box.y) to (box.x + box.width, box.y + box.height)
    // Box center is at (box.x + box.width/2, box.y + box.height/2)
    // Logo center can move within: boxCenter ± (boxSize/2 - logoSize/2)
    const maxOffsetX = Math.max(0, (box.width - logoWidth) / 2);
    const maxOffsetY = Math.max(0, (box.height - logoHeight) / 2);

    return {
      minX: -maxOffsetX,
      maxX: maxOffsetX,
      minY: -maxOffsetY,
      maxY: maxOffsetY,
    };
  };

  // Monitor size changes with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        // Only update if dimensions actually changed and are valid
        if (width > 0 && height > 0 && (Math.abs(width - dimensions.width) > 1 || Math.abs(height - dimensions.height) > 1)) {
           setDimensions({ width, height });
        }
      }
    };

    // Initial size
    updateSize();

    // Observe changes
    const observer = new ResizeObserver(() => {
      // Wrap in requestAnimationFrame to avoid "ResizeObserver loop limit exceeded"
      requestAnimationFrame(updateSize);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [dimensions.width, dimensions.height]);

  // Detect red boxes when mask changes
  // IMPORTANT: Detect at FIXED size (1024x1024) to match generateFinalMockupWithPosition
  useEffect(() => {
    if (!maskUrl) return;

    const detectBoxes = async () => {
      try {
        const DETECTION_SIZE = 1024; // Fixed size for consistent detection
        const boxes = await getRedBoxBoundaries(maskUrl, DETECTION_SIZE, DETECTION_SIZE);
        setRedBoxes(boxes);
        console.log(`Detected ${boxes.length} red box boundaries at ${DETECTION_SIZE}x${DETECTION_SIZE}`);
      } catch (err) {
        console.error('Failed to detect red boxes:', err);
        setRedBoxes([]);
      }
    };

    detectBoxes();
  }, [maskUrl]);

  // Apply color transformation when logo, printType, or detailThreshold changes
  useEffect(() => {
    if (!logoImage) {
      setTransformedLogo('');
      return;
    }

    const transform = async () => {
      try {
        let transformed = logoImage;
        if (printType === 'laser') {
          // applyLaserTransform(imageBase64, whiteThreshold = 220, detailThreshold = 50)
          transformed = await applyLaserTransform(logoImage, 220, detailThreshold);
        } else if (printType === 'silk') {
          transformed = await applySilkTransform(logoImage);
        }
        setTransformedLogo(transformed);
      } catch (err) {
        console.error('Failed to transform logo:', err);
        setTransformedLogo(logoImage); // Fallback to original
      }
    };

    transform();
  }, [logoImage, printType, detailThreshold]);

  // Auto-adjust position when scale changes to keep logo within bounds
  useEffect(() => {
    if (!logoImg || !redBoxes || redBoxes.length === 0) return;

    const bounds = calculatePositionBounds();

    // Clamp current position to new bounds
    const clampedX = Math.max(bounds.minX, Math.min(bounds.maxX, position.x));
    const clampedY = Math.max(bounds.minY, Math.min(bounds.maxY, position.y));

    // Only update if position changed
    if (clampedX !== position.x || clampedY !== position.y) {
      console.log(`📐 Auto-adjusting position from (${position.x}, ${position.y}) to (${clampedX}, ${clampedY}) due to bounds [${bounds.minX}, ${bounds.maxX}] x [${bounds.minY}, ${bounds.maxY}]`);
      onPositionChange({ x: clampedX, y: clampedY });
    }
  }, [scale, logoImg, redBoxes]); // Re-run when scale or logo or redBoxes change

  // Draw canvas: mask image (with red boxes) + logos
  useEffect(() => {
    if (!canvasRef.current || !maskUrl || dimensions.width === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // HIGH DPI CANVAS: Render at 3x resolution for sharp detail preservation
    // This ensures fine lines and small details remain crisp on all devices
    const PIXEL_RATIO = 3;

    // Set canvas PIXEL size (3x display size)
    canvas.width = dimensions.width * PIXEL_RATIO;
    canvas.height = dimensions.height * PIXEL_RATIO;

    // Set canvas CSS size to match container (for display)
    canvas.style.width = '100%';
    canvas.style.height = '100%';

    // Scale context by PIXEL_RATIO so all drawing coordinates work correctly
    ctx.scale(PIXEL_RATIO, PIXEL_RATIO);

    console.log(`📱 [Canvas HD] Display: ${dimensions.width}x${dimensions.height}px → Pixel: ${dimensions.width * PIXEL_RATIO}x${dimensions.height * PIXEL_RATIO}px (${PIXEL_RATIO}x DPI)`);

    // Load mask image (has red boxes)
    const maskImg = new Image();
    maskImg.crossOrigin = 'anonymous';
    maskImg.onload = () => {
      // Draw mask image (with red boxes visible)
      // Use logical dimensions (not pixel dimensions) since ctx.scale() is applied
      const scaleFit = Math.min(dimensions.width / maskImg.width, dimensions.height / maskImg.height) * 0.9;
      let drawWidth = maskImg.width * scaleFit;
      let drawHeight = maskImg.height * scaleFit;

      // Apply canvas zoom
      const zoomFactor = canvasZoom / 100;
      drawWidth = drawWidth * zoomFactor;
      drawHeight = drawHeight * zoomFactor;

      let maskX = (dimensions.width / 2) - (drawWidth / 2);
      let maskY = (dimensions.height / 2) - (drawHeight / 2);

      // Apply panning offset
      maskX += panOffset.x;
      maskY += panOffset.y;

      ctx.clearRect(0, 0, dimensions.width, dimensions.height);
      ctx.drawImage(maskImg, maskX, maskY, drawWidth, drawHeight);

      // Draw detected red box boundaries (DEBUG VISUALIZATION)
      if (showDebug && redBoxes.length > 0) {
        // Scale from detection size (1024x1024) to display size
        const DETECTION_SIZE = 1024;
        const scaleX = drawWidth / DETECTION_SIZE;
        const scaleY = drawHeight / DETECTION_SIZE;

        ctx.strokeStyle = 'rgba(0, 255, 0, 0.9)'; // Green outline to show detected boxes
        ctx.setLineDash([]);
        ctx.lineWidth = 3;

        redBoxes.forEach((box, idx) => {
          // Use maskX which already has panOffset applied
          const boxX = maskX + box.x * scaleX;
          const boxY = maskY + box.y * scaleY;
          const boxW = box.width * scaleX;
          const boxH = box.height * scaleY;

          ctx.strokeRect(boxX, boxY, boxW, boxH);

          // Draw box label with background
          ctx.fillStyle = 'rgba(0, 255, 0, 0.9)';
          ctx.fillRect(boxX + 2, boxY + 2, 60, 18);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
          ctx.font = 'bold 11px monospace';
          ctx.fillText(`Box ${idx + 1}`, boxX + 5, boxY + 14);

          // Draw size info
          ctx.fillStyle = 'rgba(0, 255, 0, 0.8)';
          ctx.fillRect(boxX + 2, boxY + boxH - 20, 80, 18);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
          ctx.font = 'bold 10px monospace';
          ctx.fillText(`${Math.round(boxW)}x${Math.round(boxH)}`, boxX + 5, boxY + boxH - 7);
        });

        console.log(`🟢 Drawing ${redBoxes.length} detected red boxes (green outlines), scale: ${scaleX.toFixed(3)}x${scaleY.toFixed(3)}`);
      }

      // Draw transformed logos (one per red box)
      if (transformedLogo && redBoxes.length > 0) {
        const logoImg = new Image();
        logoImg.onload = () => {
          // Scale from detection size (1024x1024) to display size
          const DETECTION_SIZE = 1024;
          const scaleX = drawWidth / DETECTION_SIZE;
          const scaleY = drawHeight / DETECTION_SIZE;

          redBoxes.forEach(box => {
            // Box coordinates are in detection space (1024x1024)
            // Scale to display space
            const boxCenterX = maskX + (box.x + box.width / 2) * scaleX;
            const boxCenterY = maskY + (box.y + box.height / 2) * scaleY;

            // Calculate logo size (based on detection space dimensions)
            // At 100% scale, logo should fit perfectly in box (80% of box size)
            const baseWidth = box.width * scaleX * 0.8;
            const aspectRatio = logoImg.height / logoImg.width;
            const scaleFactor = scale / 100;
            let logoWidth = baseWidth * scaleFactor;
            let logoHeight = logoWidth * aspectRatio;

            // Ensure logo fits
            if (logoHeight > box.height * scaleY * 0.8 * scaleFactor) {
              logoHeight = box.height * scaleY * 0.8 * scaleFactor;
              logoWidth = logoHeight / aspectRatio;
            }

            // Apply position offset - DIRECT (match generateFinalMockupWithPosition logic)
            // position.x/y are in detection space (1024x1024), scale to display space
            const positionX = position.x * scaleX;
            const positionY = position.y * scaleY;
            const logoX = boxCenterX + positionX - logoWidth / 2;
            const logoY = boxCenterY + positionY - logoHeight / 2;

            // Draw logo
            ctx.globalAlpha = 0.95;
            ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
            ctx.globalAlpha = 1.0;
          });
        };
        logoImg.src = transformedLogo;
      }
    };

    maskImg.src = maskUrl;
  }, [maskUrl, transformedLogo, position, scale, dimensions, redBoxes, showDebug, canvasZoom, panOffset]);

  // --- HISTORY & UNDO/REDO ---
  const saveToHistory = () => {
    setHistory((prev) => [...prev, { position: { ...position }, scale }]);
    setRedoStack([]); // Clear redo stack on new action
  };

  const handleUndo = () => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const lastState = newHistory.pop();
      if (lastState) {
        // Push current state to redo stack before undoing
        setRedoStack(prevRedo => [...prevRedo, { position: { ...position }, scale }]);
        
        onPositionChange(lastState.position);
        onScaleChange(lastState.scale);
      }
      return newHistory;
    });
  };

  const handleRedo = () => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const newRedo = [...prev];
      const nextState = newRedo.pop();
      if (nextState) {
        // Push current state to history before redoing
        setHistory(prevHist => [...prevHist, { position: { ...position }, scale }]);

        onPositionChange(nextState.position);
        onScaleChange(nextState.scale);
      }
      return newRedo;
    });
  };

  // --- TOUCH / MOUSE HANDLERS ---

  // Calculate distance between two touch points
  const getTouchDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    saveToHistory(); // Save before change

    if (e.touches.length === 2) {
      // Pinch gesture
      e.preventDefault();
      setIsPinching(true);
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      setInitialPinchDistance(distance);
      setInitialScale(scale);
    } else if (e.touches.length === 1) {
      // Single touch for drag
      setIsDragging(true);

      // Check if touching on logo
      const onLogo = isClickOnLogo(e.touches[0].clientX, e.touches[0].clientY);
      setIsDraggingLogo(onLogo);

      setDragStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      });
    }
  };

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (disabled) return;

    if (isPinching && e.touches.length === 2) {
      // Pinch to zoom
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const distanceRatio = currentDistance / initialPinchDistance;
      const newScale = Math.round(initialScale * distanceRatio);
      handleZoomChange(newScale);
    } else if (isDragging && e.touches.length === 1) {
      // Drag to move (either logo or canvas panning)
      const deltaX = e.touches[0].clientX - dragStart.x;
      const deltaY = e.touches[0].clientY - dragStart.y;

      if (isDraggingLogo) {
        // Dragging logo: move position (bounded)
        const newX = position.x + deltaX / 2;
        const newY = position.y + deltaY / 2;

        // Clamp to dynamic bounds based on red box and logo size
        const bounds = calculatePositionBounds();
        const clampedX = Math.max(bounds.minX, Math.min(bounds.maxX, newX));
        const clampedY = Math.max(bounds.minY, Math.min(bounds.maxY, newY));

        onPositionChange({
          x: clampedX,
          y: clampedY,
        });
      } else {
        // Dragging canvas: pan the view
        setPanOffset({
          x: panOffset.x + deltaX,
          y: panOffset.y + deltaY,
        });
      }

      setDragStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      });
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsDraggingLogo(false);
    setIsPinching(false);
  };

  // Handle mouse drag
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    saveToHistory(); // Save before change
    setIsDragging(true);

    // Check if clicking on logo
    const onLogo = isClickOnLogo(e.clientX, e.clientY);
    setIsDraggingLogo(onLogo);

    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || disabled) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    if (isDraggingLogo) {
      // Dragging logo: move position (bounded)
      const newX = position.x + deltaX / 2;
      const newY = position.y + deltaY / 2;

      // Clamp to dynamic bounds based on red box and logo size
      const bounds = calculatePositionBounds();
      const clampedX = Math.max(bounds.minX, Math.min(bounds.maxX, newX));
      const clampedY = Math.max(bounds.minY, Math.min(bounds.maxY, newY));

      onPositionChange({
        x: clampedX,
        y: clampedY,
      });
    } else {
      // Dragging canvas: pan the view
      setPanOffset({
        x: panOffset.x + deltaX,
        y: panOffset.y + deltaY,
      });
    }

    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsDraggingLogo(false);
  };

  const handleZoomChange = (newScale: number) => {
    const MIN_ZOOM = 10;
    const MAX_ZOOM = 110; // 110% = allow oversized logos
    const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newScale));

    onScaleChange(clampedZoom);

    if (newScale < MIN_ZOOM) {
      setBoundaryWarning('🔍 Minimum zoom reached');
      setTimeout(() => setBoundaryWarning(''), 500);
    } else if (newScale > MAX_ZOOM) {
      setBoundaryWarning('🔍 Maximum zoom reached (110%)');
      setTimeout(() => setBoundaryWarning(''), 500);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white rounded-lg overflow-hidden">
      {/* Canvas Preview */}
      <div className="flex-1 relative bg-gray-50 min-h-0" ref={containerRef}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className={`w-full h-full block touch-none ${
            disabled || isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
        />

        {/* Boundary Warning */}
        {boundaryWarning && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 animate-in fade-in duration-200 bg-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg z-10">
            <AlertCircle className="w-3 h-3" />
            {boundaryWarning}
          </div>
        )}

        {/* LEFT FLOATING TOOLBAR: Grouped Tools - Vertical layout with horizontal button content */}
        <div className="absolute top-2 left-2 md:top-3 md:left-3 bg-white/95 backdrop-blur-md rounded-lg shadow-md border border-gray-100 flex flex-col p-0.5 md:p-1 gap-0.5 md:gap-1 z-10 w-auto" style={{gridAutoRows: 'minmax(0, 1fr)'}}>

          {/* Create Again (X) */}
          <button
            onClick={onClear}
            className="group flex items-center justify-center md:justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:w-32 md:px-3 md:py-2 hover:bg-gray-100 rounded-lg transition-all w-[44px] md:w-auto"
            title="Tạo lại logo"
            disabled={disabled}
          >
            <X className="w-4 h-4 md:w-5 md:h-5 text-black flex-shrink-0" />
            <span className="hidden md:inline text-sm font-bold font-sans text-black">Tạo lại</span>
          </button>

          {/* Reset */}
          <button
            onClick={() => {
              saveToHistory();
              onPositionChange({ x: 0, y: 0 });
              onScaleChange(100);
              setCanvasZoom(100);
              setPanOffset({ x: 0, y: 0 });
              onDetailThresholdChange?.(50);
            }}
            className="group flex items-center justify-center md:justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:w-32 md:px-3 md:py-2 hover:bg-gray-100 rounded-lg transition-all w-[44px] md:w-auto"
            title="Reset tất cả"
            disabled={disabled}
          >
            <RotateCcw className="w-4 h-4 md:w-5 md:h-5 text-black flex-shrink-0" />
            <span className="hidden md:inline text-sm font-bold font-sans text-black">Đặt lại</span>
          </button>

          {/* Undo - Hidden on mobile, visible on desktop */}
          <button
            onClick={handleUndo}
            disabled={disabled || history.length === 0}
            className={`group hidden md:flex items-center justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:w-32 md:px-3 md:py-2 rounded-lg transition-all md:w-auto ${
               history.length === 0
               ? 'opacity-40 cursor-not-allowed'
               : 'hover:bg-gray-100 cursor-pointer'
            }`}
            title="Hoàn tác"
          >
            <Undo className={`w-4 h-4 md:w-5 md:h-5 flex-shrink-0 ${history.length === 0 ? 'text-gray-400' : 'text-black'}`} />
            <span className={`hidden md:inline text-sm font-bold font-sans ${history.length === 0 ? 'text-gray-400' : 'text-black'}`}>Hoàn tác</span>
          </button>

          {/* Redo - Hidden on mobile, visible on desktop */}
          <button
            onClick={handleRedo}
            disabled={disabled || redoStack.length === 0}
            className={`group hidden md:flex items-center justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:w-32 md:px-3 md:py-2 rounded-lg transition-all md:w-auto ${
               redoStack.length === 0
               ? 'opacity-40 cursor-not-allowed'
               : 'hover:bg-gray-100 cursor-pointer'
            }`}
            title="Làm lại"
          >
            <Redo className={`w-4 h-4 md:w-5 md:h-5 flex-shrink-0 ${redoStack.length === 0 ? 'text-gray-400' : 'text-black'}`} />
            <span className={`hidden md:inline text-sm font-bold font-sans ${redoStack.length === 0 ? 'text-gray-400' : 'text-black'}`}>Làm lại</span>
          </button>

          {/* Zoom Canvas - Minus */}
          <button
            onClick={() => setCanvasZoom(Math.max(50, canvasZoom - 10))}
            className="group flex items-center justify-center md:justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:w-32 md:px-3 md:py-2 hover:bg-gray-100 rounded-lg transition-all w-[44px] md:w-auto"
            title="Thu nhỏ"
            disabled={disabled}
          >
            <ZoomOut className="w-4 h-4 md:w-5 md:h-5 text-black flex-shrink-0" />
            <span className="hidden md:inline text-sm font-bold font-sans text-black">Thu nhỏ</span>
          </button>

          {/* Zoom Canvas - Plus */}
          <button
            onClick={() => setCanvasZoom(Math.min(200, canvasZoom + 10))}
            className="group flex items-center justify-center md:justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:w-32 md:px-3 md:py-2 hover:bg-gray-100 rounded-lg transition-all w-[44px] md:w-auto"
            title="Phóng to"
            disabled={disabled}
          >
            <ZoomIn className="w-4 h-4 md:w-5 md:h-5 text-black flex-shrink-0" />
            <span className="hidden md:inline text-sm font-bold font-sans text-black">Phóng to</span>
          </button>
        </div>

        {/* Size Slider Control - Mobile optimized */}
        <div className="absolute bottom-2 left-2 right-2 md:bottom-3 md:left-4 md:right-auto md:w-48 bg-white/95 backdrop-blur-md rounded-lg shadow-sm border border-gray-200/50 p-2 md:p-3 flex flex-col gap-1 md:gap-2 z-10">
          {/* Logo Size Slider */}
          <div className="flex items-center justify-between text-[10px] md:text-xs">
            <span className="font-semibold text-gray-700 line-clamp-1">Kích thước</span>
            <span className="font-mono text-cyan-600 font-bold flex-shrink-0">{scale}%</span>
          </div>
          <input
            type="range"
            min="10"
            max="110"
            step="1"
            value={scale}
            onPointerDown={() => saveToHistory()}
            onChange={(e) => handleZoomChange(Number(e.target.value))}
            className="w-full h-1 md:h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-cyan-500 hover:accent-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-100"
          />
        </div>

      </div>
    </div>
  );
};

export default InteractivePreview;
